import React, { useEffect, useRef, useState } from 'react';
import {
  CustomCollapseCard,
  CustomCollapse,
  CustomForm,
  CustomCheckbox,
  CustomPageHeader,
  CustomFormItem,
  CustomSelect,
  Option,
  Text,
  useText,
  CustomButton,
  CustomTabs,
  CustomInput,
  CustomImage,
  CustomPagination,
  successDialog,
  CustomMaskInput,
} from '@src/components';
import '@src/styles/managementUser/addUser.scss';
import { Checkbox, Form, Radio, Input, Empty } from 'antd';
import { useHistory } from 'react-router-dom';
import search from '@src/assets/icons/icon-transparent-search.svg';
import { useDispatch, useSelector } from 'react-redux';
import { getAllOperationClaims, setClaimList, setNewClaimList } from '@src/store/slice/groupsSlice';
import { createUserAndRole, userContactLookup } from '@src/store/slice/userSlice';
import { getActiveProducts } from '@src/store/slice/customerVouGroupsSlice';
import {
  getListAddresses,
  setTempFormList,
  setAddressSelectionType,
} from '@src/store/slice/addressSlice';
import { getUserTypesList } from '@src/store/slice/userTypesSlice';
import { formMailRegex, formPhoneRegex } from '@src/utils/formRule';
import { warningDialog } from '@src/components/CustomDialog';
import { Loading } from '@src/components/Loading';
import { useLanguageChange } from '@src/hooks/useLanguageChange';

const AddUser = () => {
  const dispatch = useDispatch();
  const { claims, claimList, newClaimList } = useSelector((state) => state?.groups);
  const { activeProductList } = useSelector((state) => state?.customerVouGroups);
  const { addresses, tempFormList, selectedAddressType } = useSelector((state) => state?.address);
  const { userTypeList } = useSelector((state) => state?.userTypes);
  const {
    table: { detail },
  } = useSelector((state) => state?.custUsers);
  const { selectedCustomer } = useSelector((state) => state?.customer);

  const history = useHistory();
  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState({ products: {} });
  const [checkedProducts, setCheckedProducts] = useState([]);
  const [uncheckedProductIds, setUncheckedProductIds] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [state, setState] = useState(null);
  const [claimsSearchText, setSearchText] = useState({});
  const [addressListSearchText, setAddressSearchText] = useState({});

  const searchText = useText('writeTheFunctionYouAreLookingFor');
  const addressSearchText = useText('writeTheAddressYouAreLookingFor');
  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');
  const companyInformation2 = useText('companyInformation2');

  const [initialValues, setInitialValues] = useState(false);

  const changePagination = (PageNumber, PageSize, vouId, isAddress = false) => {
    if (isAddress) {
      getAddressList(PageNumber, PageSize, vouId, addressListSearchText[vouId]);
    } else {
      getClaimList(PageNumber, PageSize, vouId, claimsSearchText[vouId]);
    }
  };

  const handleSearchInput = (e, vouId, isAddress = false) => {
    if (isAddress) {
      setAddressSearchText({
        ...addressListSearchText,
        [vouId]: e.target.value,
      });
    } else {
      setSearchText({
        ...claimsSearchText,
        [vouId]: e.target.value,
      });
    }
  };

  const performSearch = (vouId, isAddress = false) => {
    if (isAddress) {
      getAddressList(1, 10, vouId, addressListSearchText[vouId]);
    } else {
      getClaimList(1, 10, vouId, claimsSearchText[vouId]);
    }
  };

  const hasProductEnoughClaims = (product) =>
    product?.vous?.some((vou) => {
      const selectedClaims = claimList[vou?.vouId] || newClaimList[vou?.vouId] || [];
      return selectedClaims.length > 1;
    });

  const isProductChecked = (product) =>
    checkedProducts?.some((item) => item.groupId === product?.groupId) ||
    (!uncheckedProductIds.includes(product?.groupId) && hasProductEnoughClaims(product));

  useEffect(() => {
    dispatch(getUserTypesList());
    dispatch(userContactLookup());
  }, [dispatch]);

  // User roles and the product/permission tree are rendered with the names the backend returns, so
  // they have to be requested again in the newly selected language while this form stays open.
  useLanguageChange(() => {
    dispatch(getUserTypesList());
    dispatch(userContactLookup());
    if (selectedRole) {
      dispatch(getActiveProducts());
    }
  });

  useEffect(() => {
    try {
      if (
        selectedRole === 4 &&
        (checkedProducts?.length === 0 ||
          !checkedProducts?.find((item) => item?.groupId === 5000001)) &&
        activeProductList?.length > 0
      ) {
        clickActiveProduct(
          activeProductList?.find((act) => act.groupId === 5000001),
          true,
        );
      }
    } catch (error) {
      console.error(error);
    }
  }, [selectedRole, checkedProducts, activeProductList]);

  useEffect(() => {
    handleClean();
  }, [history]);

  useEffect(() => {
    handleClean();
    setSearchText({});
    setAddressSearchText({});
  }, [selectedRole]);

  useEffect(() => {
    if (selectedRole === 1 && activeProductList?.length > 0) {
      setCheckedProducts(activeProductList);
      setUncheckedProductIds([]);
    }
  }, [selectedRole, activeProductList]);

  const onFinish = async (data) => {
    const values = await form.validateFields();
    const currentValues = await form.getFieldsValue();
    const productValues = await form.getFieldValue('products');
    const filledGroupIds = productValues
      ? Object.entries(productValues)
        .filter(([_, value]) => Array.isArray(value?.groupIds) && value.groupIds.length > 0)
        .map(([vouId, value]) => ({
          vouId,
          groupIds: value.groupIds,
          addressList: value.addressList,
          addressSelectionType: value.addressSelectionType,
          groupId: value.groupId,
        }))
      : null;
    const formProduct = filledGroupIds ?? initialValues;
    const filteredValues = formProduct
      ?.map((item) => {
        if (item) {
          const groupIds = selectedRole === 1 ? [] : claimList[item?.vouId];
          return item?.addressSelectionType
            ? {
              ...item,
              addressList: tempFormList[item?.vouId],
              groupIds,
            }
            : {
              addressSelectionType: false,
              groupId: item.groupId,
              vouId: item.vouId,
              groupIds,
            };
        }
      })
      .filter((item) => item);
    let flag = false;
    filteredValues?.map((item) => {
      if (
        ((item.vouId === '4' || item.vouId === 4) && item.groupIds?.length > 0) ||
        ((item.vouId === '94' || item.vouId === 94) && item.groupIds?.length > 0) ||
        ((item.vouId === '1' || item.vouId === 1) && item.groupIds?.length > 0) ||
        ((item.vouId === '11' || item.vouId === 11) && item.groupIds?.length > 0)
      ) {
        flag = true;
      }
    });
    if (selectedRole === 4 && (!flag || !checkedProducts || checkedProducts.length === 0)) {
      warningDialog({
        title: <Text t="information" />,
        message: 'Devam edebilmek için lütfen en az bir adet ürün seçiniz.',
      });
      return;
    }
    const action = await dispatch(
      createUserAndRole({
        groupId: currentValues.groupId,
        email: currentValues.email?.trim(),
        name: currentValues.name?.trim(),
        surName: currentValues.surName?.trim(),
        mobilePhones: currentValues.mobilePhones
          .replace(/\)/g, '')
          .replace(/\(/g, '')
          .replace(/-/g, '')
          .replace(/ /g, '')
          ?.trim(),
        customerId: selectedCustomer?.customerId,
        userRoleInfo: [...filteredValues],
      }),
    );
    if (createUserAndRole.fulfilled.match(action)) {
      successDialog({
        title: <Text t="registrationSuccessful" />,
        message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
        onOk: async () => {
          history.push('/company-information/management-user');
        },
      });
    }
  };

  const getClaimList = (PageNumber, PageSize, vouId, text) => {
    let body = {
      vouId,
      searchText: text,
    };
    if (selectedRole) {
      body = { ...body, userTypeId: selectedRole };
    }
    dispatch(
      getAllOperationClaims({
        body,
        pageNumber: PageNumber,
        pageSize: PageSize,
      }),
    );
  };

  const getAddressList = (PageNumber, PageSize, vouId, text) => {
    let body = {
      customerId: selectedCustomer?.customerId,
      PageNumber,
      PageSize,
    };
    if (text) {
      body = { ...body, address: text };
    }
    dispatch(getListAddresses({ body, vouId }));
  };

  const handleClaimsChange = (e, vouId, claim) => {
    const tempClaimList = {
      ...claimList,
      [vouId]: !e
        ? claimList[vouId]?.filter((f) => f !== claim.id)
        : [...claimList[vouId], claim.id],
    };
    dispatch(setClaimList(tempClaimList));

    const tempNewClaimList = {
      ...newClaimList,
      [vouId]: !e
        ? newClaimList[vouId]?.filter((f) => f !== claim.id)
        : [...newClaimList[vouId], claim.id],
    };
    dispatch(setNewClaimList(tempNewClaimList));
  };

  const handleAddressChange = (e, vouId, addressId) => {
    const tempList = {
      ...tempFormList,
      [vouId]: !e
        ? tempFormList[vouId]?.filter((f) => f !== addressId)
        : [...tempFormList[vouId], addressId],
    };
    dispatch(setTempFormList(tempList));
  };

  const tabChange = async (vouId) => {
    return null;
  };

  const handleClean = () => {
    dispatch(setTempFormList({}));
    dispatch(setClaimList({}));
    dispatch(setNewClaimList({}));
    dispatch(setAddressSelectionType({}));
  };

  const cancel = () => {
    handleClean();
    history.push('/company-information/management-user');
  };

  useEffect(() => {
    (async () => {
      if (selectedRole) {
        const action = await dispatch(getActiveProducts());
        if (getActiveProducts.fulfilled.match(action)) {
          const data = action?.payload?.data || [];
          if (data.length > 0) {
            const formInitialValues = [];
            data.forEach((product) => {
              product.vous.forEach((vou) => {
                getClaimList(1, 10, vou?.vouId);
                getAddressList(1, 10, vou?.vouId);
                formInitialValues[vou?.vouId] = {
                  vouId: vou.vouId,
                  groupId: product.groupId,
                  addressSelectionType: true,
                  groupIds: [],
                  addressList: [],
                };
              });
            });

            form.setFieldsValue({
              groupId: selectedRole,
              customerId: detail?.customerId,
              products: formInitialValues,
            });
            setInitialValues(formInitialValues);
            setState(data);
          }
        }
      }
    })();
  }, [dispatch, selectedRole]);

  useEffect(() => {
    if (!Array.isArray(activeProductList) || activeProductList.length === 0) {
      return;
    }

    const values = form.getFieldsValue();

    const formCheckedValues = [];
    activeProductList.forEach((product) => {
      product.vous.forEach((vou) => {
        formCheckedValues[vou.vouId] = {
          vouId: vou.vouId,
          groupId: product.groupId,
          addressSelectionType: selectedAddressType[vou.vouId] ?? false,
          groupIds: claimList[vou.vouId],
          addressList: tempFormList[vou.vouId],
        };
      });
    });
    form.setFieldsValue({
      ...values,
      products: formCheckedValues,
    });
    setInitialValues(formCheckedValues);
    setFormValues((prev) => ({ ...(prev || {}), products: formCheckedValues }));
  }, [state, claims, addresses, activeProductList, claimList, selectedAddressType, tempFormList, form]);

  const [runSetAllProductFields, setRunSetAllProductFields] = useState(null);
  const [loadingSpinner, setLoadingSpinner] = useState(false);

  const [checkVouId5000002ForUBY, setCheckVouId5000002ForUBY] = useState(false);
  const [tempClaimList5000002, setTempClaimList5000002] = useState(null);
  const [temp2ClaimList5000002, setTemp2ClaimList5000002] = useState(null);
  const delayedSetAllProductFieldsTimeoutRef = useRef(null);
  const pendingProductTimeoutRef = useRef(null);

  useEffect(() => {
    if (runSetAllProductFields) {
      const timeoutId = setTimeout(() => {
        setAllProductFields(runSetAllProductFields);
      }, 1000);
      delayedSetAllProductFieldsTimeoutRef.current = timeoutId;
      return () => clearTimeout(timeoutId);
    }
  }, [runSetAllProductFields]);

  useEffect(() => {
    return () => {
      if (delayedSetAllProductFieldsTimeoutRef.current) {
        clearTimeout(delayedSetAllProductFieldsTimeoutRef.current);
      }
      if (pendingProductTimeoutRef.current) {
        clearTimeout(pendingProductTimeoutRef.current);
      }
    };
  }, []);

  const setAllProductFields = async (product) => {
    try {
      const prevProducts = form.getFieldValue('products') || {};

      const tempClaimList = { ...claimList };
      const tempNewClaimList = { ...newClaimList };
      const nextProducts = form.getFieldValue('products') || {};

      (product?.vous || []).forEach((vou) => {
        const vouId = vou.vouId;

        const baseIds = (claims[vouId]?.data || []).map((c) => c.id);

        const mergedClaimList = Array.from(
          new Set([...(tempClaimList[vouId] || []), ...baseIds, ...(claimList[vouId] || [])]),
        );

        const mergedNewClaimList = Array.from(
          new Set([...(tempNewClaimList[vouId] || []), ...baseIds, ...(newClaimList[vouId] || [])]),
        );

        const prevVouObj = prevProducts[vouId] || {};
        if (selectedRole !== 4 || !checkVouId5000002ForUBY || vou.vouId !== 5000002) {
          tempClaimList[vouId] = mergedClaimList;
          tempNewClaimList[vouId] = mergedNewClaimList;
          nextProducts[vouId] = {
            ...prevVouObj,
            groupIds: mergedNewClaimList,
          };
        } else {
          setTempClaimList5000002(mergedNewClaimList);
          setTemp2ClaimList5000002(mergedClaimList);
        }
        const v5000001 = 5000001;
        const v5000002 = 5000002;
        if (
          vouId === 1 &&
          nextProducts[vouId]?.groupIds?.length > 0 &&
          selectedRole === 4 &&
          prevProducts[v5000001]?.groupIds?.length > 0 &&
          !(prevProducts[v5000002]?.groupIds?.length > 0)
        ) {
          tempClaimList[v5000002] = temp2ClaimList5000002;
          tempNewClaimList[v5000002] = tempClaimList5000002;
          nextProducts[v5000002] = {
            ...prevVouObj,
            groupIds: tempClaimList5000002,
          };
        }
      });

      await Promise.all([
        dispatch(setClaimList(tempClaimList)),
        dispatch(setNewClaimList(tempNewClaimList)),
      ]);

      form.setFieldsValue({ products: nextProducts });
    } catch (error) {
      console.error(error);
    }
    setRunSetAllProductFields(null);
    setCheckVouId5000002ForUBY(null);

    setLoadingSpinner(false);
  };

  const clickActiveProduct = async (product, ifUBY = false) => {
    if (pendingProductTimeoutRef.current) {
      clearTimeout(pendingProductTimeoutRef.current);
    }
    if (
      !checkedProducts ||
      checkedProducts?.length === 0 ||
      checkedProducts?.filter((item) => item.groupId === product.groupId).length === 0
    ) {
      setLoadingSpinner(true);
      setCheckedProducts([...checkedProducts, product]);
      product.vous.forEach(async (vou) => {
        getClaimList(1, 100, vou?.vouId);
      });
      setCheckVouId5000002ForUBY(ifUBY);
      const timeoutId = setTimeout(() => {
        setRunSetAllProductFields(product);
      }, 5000);
      pendingProductTimeoutRef.current = timeoutId;
    } else {
      const newProducts = checkedProducts.filter((item) => item.groupId !== product.groupId);
      setCheckedProducts(newProducts);

      try {
        product?.vous.map((vou) => {
          let arr = [];
          claims[vou.vouId]?.data?.map((claim) => {
            arr.push(claim.id);
          });

          let newClaimArr = [];
          claimList[vou.vouId]?.map((cvv) => {
            if (arr?.filter((ar) => ar === cvv).length === 0) newClaimArr.push(cvv);
          });
          const tempClaimList = {
            ...claimList,
            [vou.vouId]: newClaimArr,
          };
          dispatch(setClaimList(tempClaimList));

          let newClaimListArr = [];
          newClaimList[vou.vouId]?.map((cvv) => {
            if (arr?.filter((ar) => ar === cvv).length === 0) newClaimListArr.push(cvv);
          });
          const tempNewClaimList = {
            ...newClaimList,
            [vou.vouId]: newClaimListArr,
          };
          dispatch(setNewClaimList(tempNewClaimList));

          const prevProducts = form.getFieldValue('products') || {};
          const prevVouObj = prevProducts[vou.vouId] || {};

          form.setFieldsValue({
            products: {
              ...prevProducts,
              [vou.vouId]: {
                ...prevVouObj,
                groupIds: newClaimListArr,
              },
            },
          });
        });
      } catch (error) { }
    }
  };

  return (
    <CustomPageHeader title={<Text t="userManagement" />} showBreadCrumb showHelpButton>
      <Loading spinning={loadingSpinner}>
        <CustomCollapseCard className="add-user-card" cardTitle={<Text t="userAdd" />}>
          <CustomForm
            name="ticketRestaurantForm"
            className="add-user-form"
            autoComplete="off"
            layout={'horizontal'}
            form={form}
          >
            <div className="section-wrapper">
              <div className="section-list">
                <div className="user-select-content">
                  <div className="form-item-main">
                    <CustomFormItem
                      label={
                        <div>
                          <Text t="userRole" />
                        </div>
                      }
                      name="groupId"
                      rules={[{ required: true, message: <Text t="requiredField2" /> }]}
                    >
                      <CustomSelect
                        id="companyInformation-managementUser-addUser-groupId-select"
                        onChange={(value) => setSelectedRole(value)}
                        placeholder={useText('choose')}
                        height={36}
                      >
                        {userTypeList?.map((item) => (
                          <Option
                            id={`companyInformation-managementUser-addUser-groupId-${item?.name}-option`}
                            key={item.id}
                            value={item.id}
                          >
                            {item.name}
                          </Option>
                        ))}
                      </CustomSelect>
                    </CustomFormItem>

                    <CustomFormItem
                      label={
                        <div>
                          <Text t="name3" />
                        </div>
                      }
                      name="name"
                      className="custom-form-item"
                      rules={[{ required: true, message: <Text t="requiredField2" /> }]}
                    >
                      <CustomInput
                        id="companyInformation-managementUser-addUser-name-input"
                        autoComplete="off"
                        placeholder={useText('name3')}
                        height={36}
                        maxLength={100}
                      />
                    </CustomFormItem>

                    <CustomFormItem
                      label={
                        <div>
                          <Text t="surname2" />
                        </div>
                      }
                      name="surName"
                      className="custom-form-item"
                      rules={[{ required: true, message: <Text t="requiredField2" /> }]}
                    >
                      <CustomInput
                        id="companyInformation-managementUser-addUser-surName-input"
                        autoComplete="off"
                        placeholder={useText('surname2')}
                        height={36}
                        maxLength={100}
                      />
                    </CustomFormItem>

                    <CustomFormItem
                      label={
                        <div>
                          <Text t="eMailAddress" />
                        </div>
                      }
                      name="email"
                      className="custom-form-item"
                      rules={[
                        { required: true, message: <Text t="pleaseEnterEmail" /> },
                        { validator: formMailRegex, message: <Text t="enterValidEmail" /> },
                      ]}
                    >
                      <CustomInput
                        id="companyInformation-managementUser-addUser-email-input"
                        autoComplete="off"
                        height={36}
                        maxLength={100}
                      />
                    </CustomFormItem>

                    <CustomFormItem
                      label={
                        <div>
                          <Text t="phoneNumber2" />
                        </div>
                      }
                      name="mobilePhones"
                      rules={[
                        { required: true, message: <Text t="pleaseEnterPhone" /> },
                        { validator: formPhoneRegex, message: <Text t="enterValidPhoneNumber" /> },
                      ]}
                    >
                      <CustomMaskInput
                        id="companyInformation-managementUser-addUser-mobilePhones-maskInput"
                        mask={'+\\90 (999) 999 99 99'}
                      >
                        <CustomInput
                          id="companyInformation-managementUser-addUser-mobilePhones-input"
                          data-testid="mobilePhones"
                          placeholder="+90( _ _ _ ) _ _ _  _ _  _ _"
                          height={36}
                        />
                      </CustomMaskInput>
                    </CustomFormItem>
                  </div>
                </div>
              </div>
            </div>

            {activeProductList?.length > 0 ? (
              <h5>
                <Text t="activeProducts" />
              </h5>
            ) : null}

            {activeProductList?.map((product) => {
              return (
                <CustomCollapse
                  key={product.groupId}
                  cardTitle={
                    <div className="d-flex align-items-center">
                      <CustomCheckbox
                        id={`companyInformation-managementUser-addUser-activeProductList-${product.groupName === 'Þirket Bilgileri'
                          ? companyInformation2
                          : product.groupName
                          }-checkbox`}
                        className="collapse-header-checkbox"
                        value={product.groupId}
                        checked={isProductChecked(product)}
                        onClick={(e) => {
                          clickActiveProduct(product);
                        }}
                      />
                      {product.groupName === 'Þirket Bilgileri'
                        ? companyInformation2
                        : product.groupName}
                    </div>
                  }
                >
                  <CustomTabs
                    tabs={product?.vous.map((vou) => {
                      return {
                        key: vou.vouId,
                        name: vou.vouName,
                        content: (
                          <div className="form-content">
                            {vou?.vouId !== 5000002 && (
                              <>
                                <div className="form-item ">
                                  <p className="form-title">
                                    <Text t="deliveryAddresses2" />
                                  </p>
                                  <CustomFormItem
                                    label={false}
                                    name={['products', vou.vouId, 'vouId']}
                                    className="u-p-l-3"
                                    hidden
                                  />

                                  <CustomFormItem
                                    label={false}
                                    name={['products', vou.vouId, 'groupId']}
                                    className="u-p-l-3"
                                    hidden
                                  />

                                  <CustomFormItem
                                    label={false}
                                    name={['products', vou.vouId, 'addressSelectionType']}
                                    className="u-p-l-3"
                                  >
                                    <Radio.Group
                                      id="companyInformation-managementUser-addUser-custom-radio-input-content"
                                      onChange={(e) => {
                                        setFormValues(form.getFieldsValue());
                                        const val = {
                                          ...selectedAddressType,
                                          [vou.vouId]: e?.target?.value,
                                        };
                                        dispatch(setAddressSelectionType(val));
                                      }}
                                      defaultValue={false}
                                      className="custom-radio-input-content"
                                    >
                                      <Radio
                                        id="companyInformation-managementUser-addUser-products-all-delivery-address-radio"
                                        value={false}
                                      >
                                        <div className="custom-label">
                                          <Text t="forAllDeliveryAddresses" />
                                        </div>
                                      </Radio>
                                      <Radio
                                        id="companyInformation-managementUser-addUser-products-choose-delivery-address-radio"
                                        value={true}
                                      >
                                        <div className="custom-label">
                                          <Text t="onlyAtSelectedDeliveryAddresses" />
                                        </div>
                                      </Radio>
                                    </Radio.Group>
                                  </CustomFormItem>
                                </div>

                                {formValues?.products[vou?.vouId]?.addressSelectionType ===
                                  true && (
                                    <div>
                                      <div className="u-m-t-5">
                                        <Input
                                          id="customer-managementUser-addUser-custom-search-input"
                                          className="custom-search-input"
                                          onChange={(e) => handleSearchInput(e, vou.vouId, true)}
                                          onPressEnter={() => performSearch(vou?.vouId, true)}
                                          prefix={
                                            <CustomImage
                                              src={search}
                                              onClick={() => performSearch(vou?.vouId, true)}
                                            />
                                          }
                                          placeholder={addressSearchText}
                                          value={addressListSearchText[vou?.vouId] || ''}
                                        />
                                      </div>
                                      {addresses[vou?.vouId]?.data?.length > 0 ? (
                                        <div className="u-p-2">
                                          <CustomFormItem
                                            label={false}
                                            name={['products', vou.vouId, 'addressList']}
                                            className="u-p-l-3"
                                          >
                                            <Checkbox.Group>
                                              {addresses[vou?.vouId]?.data?.map((address) => {
                                                return (
                                                  <div className="u-m-2" key={address.id}>
                                                    <CustomCheckbox
                                                      id={`companyInformation-managementUser-addUser-addresses-${address.addressName}-checkbox`}
                                                      value={address.id}
                                                      className="collapse-header-checkbox"
                                                      onChange={(e) =>
                                                        handleAddressChange(
                                                          e.target.checked,
                                                          vou?.vouId,
                                                          address?.id,
                                                        )
                                                      }
                                                    />
                                                    <span>
                                                      {' '}
                                                      <Text t={address.addressName} />
                                                    </span>
                                                  </div>
                                                );
                                              })}
                                            </Checkbox.Group>
                                          </CustomFormItem>
                                        </div>
                                      ) : (
                                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                      )}

                                      <hr />
                                      {addresses[vou?.vouId]?.data?.length > 0 && (
                                        <div className="d-flex justify-content-center">
                                          <CustomPagination
                                            onChange={(pageNumber, pageSize) =>
                                              changeAddressPagination(
                                                pageNumber,
                                                pageSize,
                                                vou?.vouId,
                                              )
                                            }
                                            current={
                                              addresses[vou?.vouId]?.pagedProperty?.currentPage
                                            }
                                            defaultCurrent={1}
                                            pageSize={addresses[vou?.vouId]?.pagedProperty?.pageSize}
                                            total={addresses[vou?.vouId]?.pagedProperty?.totalCount}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  )}
                              </>
                            )}

                            <>
                              <p className="form-title u-m-t-5">
                                <Text t="functions" />
                              </p>
                              <Input
                                id="customer-managementUser-addUser-second-custom-search-input"
                                onChange={(e) => handleSearchInput(e, vou.vouId, false)}
                                placeholder={searchText}
                                onPressEnter={() => performSearch(vou?.vouId, false)}
                                prefix={
                                  <CustomImage
                                    src={search}
                                    onClick={() => performSearch(vou?.vouId, false)}
                                  />
                                }
                                className="custom-search-input"
                                value={claimsSearchText[vou?.vouId] || ''}
                              />
                            </>
                            {claims[vou.vouId]?.data?.length > 0 ? (
                              <div className="u-p-2">
                                <CustomFormItem
                                  label={false}
                                  className="u-p-l-3"
                                  name={['products', vou.vouId, 'groupIds']}
                                >
                                  <Checkbox.Group
                                    style={{
                                      maxHeight: 340,
                                      width: '100%',
                                      overflowY: 'auto',
                                      paddingRight: 4,
                                    }}
                                  >
                                    {claims[vou.vouId]?.data?.map((claim) => (
                                      <div className="u-m-2" key={claim.id}>
                                        <CustomCheckbox
                                          id={`companyInformation-managementUser-addUser-claims-${claim.groupName}-checkbox`}
                                          disabled={claim?.isDisabled || selectedRole === 4}
                                          value={claim.id}
                                          className="collapse-header-checkbox"
                                          onChange={(e) =>
                                            handleClaimsChange(e.target.checked, vou?.vouId, claim)
                                          }
                                        />
                                        <span>
                                          {' '}
                                          <Text t={claim.groupName} />{' '}
                                        </span>
                                      </div>
                                    ))}
                                  </Checkbox.Group>
                                </CustomFormItem>
                              </div>
                            ) : (
                              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            )}

                            <hr />

                            {claims[vou?.vouId]?.data?.length > 0 && (
                              <div className="d-flex justify-content-center">
                                <CustomPagination
                                  onChange={(pageNumber, pageSize) =>
                                    changePagination(pageNumber, pageSize, vou?.vouId, false)
                                  }
                                  current={claims[vou.vouId]?.pagedProperty?.currentPage}
                                  defaultCurrent={1}
                                  pageSize={claims[vou.vouId]?.pagedProperty?.pageSize}
                                  total={claims[vou.vouId]?.pagedProperty?.totalCount}
                                />
                              </div>
                            )}
                          </div>
                        ),
                      };
                    })}
                    onChange={tabChange}
                  />
                </CustomCollapse>
              );
            })}

            <div className="footer-form-buttons">
              <CustomButton
                id="companyInformation-managementUser-addUser-custom-btn-cancel"
                className="custom-btn-cancel"
                onClick={() => cancel()}
              >
                <Text t="giveUp" />
              </CustomButton>
              <CustomButton
                id="companyInformation-managementUser-addUser-custom-btn-save"
                onClick={() => onFinish()}
                className="custom-btn-save"
              >
                <Text t="saveNewUser" />
              </CustomButton>
            </div>
          </CustomForm>
        </CustomCollapseCard>
      </Loading>
    </CustomPageHeader>
  );
};

export default AddUser;
